import { ARSText } from "../inputs/ARSText";
import { NamedObjectDisplay } from "./NamedObjectDisplay";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the TemplateCodeParameterControl component.
 */
export interface TemplateCodeParameterControlProps {
  /** RFC 6901 JSON Pointer path to the TemplateCodeParameter in ARSContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing TemplateCodeParameter entities.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the TemplateCodeParameter in ARSContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the template code parameter editor.
 */
export const TemplateCodeParameterControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: TemplateCodeParameterControlProps): React.ReactElement => {
  const idRoute = dataPath.replace(/\//g, "-");
  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <NamedObjectDisplay
        dataPath={dataPath}
        canRename={false}
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/valueSource`}
        id={`${idRoute}-value-source`}
        label="Value source"
        labelWidth={2}
        placeholder="Enter value source"
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/value`}
        id={`${idRoute}-value`}
        label="Value"
        labelWidth={2}
        placeholder="Enter one value per line"
        isArray
        type="textarea"
        debounceMilliseconds={2000}
        rows={3}
        disabled={disabled}
      />
    </div>
  );
};

TemplateCodeParameterControl.displayName = "TemplateCodeParameterControl";
